import { type PropsWithChildren, createContext, useContext } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';

type ShareFormContextValue<TValues extends FieldValues> = {
  form: UseFormReturn<TValues>;
};

type ShareFormProviderProps<TValues extends FieldValues> = PropsWithChildren<{
  form: UseFormReturn<TValues>;
}>;

export function createShareFormContext<TValues extends FieldValues>() {
  const Context = createContext<ShareFormContextValue<TValues> | null>(null);

  function ShareFormProvider({
    form,
    children,
  }: ShareFormProviderProps<TValues>) {
    return <Context.Provider value={{ form }}>{children}</Context.Provider>;
  }

  function useShareForm(): UseFormReturn<TValues> {
    const ctx = useContext(Context);

    if (!ctx) {
      throw new Error('useShareForm must be used within a ShareFormProvider');
    }

    return ctx.form;
  }

  return { ShareFormProvider, useShareForm };
}
